import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { membershipFeesAPI } from '../../services/api';
import type { ChildFeeOverview, MembershipFee } from '../../types/membershipFee';
import { MONTH_NAMES } from '../../types/membershipFee';
import { shortNames } from '../family/format';

type View = 'monthly' | 'yearly';
type StatusFilter = 'all' | 'unpaid' | 'paid';
type FeeState = 'paid' | 'overdue' | 'due';

/** Ennyi évet mutat az éves nézet (az idei és az előzők). */
const YEARS_SHOWN = 3;

const formatFt = (amount: number) => `${amount.toLocaleString('hu-HU')} Ft`;

const now = new Date();
const thisYear = now.getFullYear();
const thisMonth = now.getMonth() + 1;

/** Fizetve, lejárt (korábbi hónap, fizetetlen) vagy esedékes (idei hónap / jövő). */
const feeState = (fee: MembershipFee): FeeState => {
  if (fee.isPaid) return 'paid';
  return fee.year < thisYear || (fee.year === thisYear && fee.month < thisMonth) ? 'overdue' : 'due';
};

const STATE_STYLE: Record<FeeState, { label: string; pill: string; tile: string; icon: React.ComponentType<{ className?: string }> }> = {
  paid: { label: 'Fizetve', pill: 'bg-green-50 text-green-700', tile: 'bg-green-500 text-white', icon: CheckCircleIcon },
  overdue: { label: 'Lejárt', pill: 'bg-red-50 text-red-700', tile: 'bg-red-500 text-white', icon: ExclamationCircleIcon },
  due: { label: 'Esedékes', pill: 'bg-amber-50 text-amber-700', tile: 'bg-amber-400 text-white', icon: ClockIcon },
};

/** Kör alakú haladásjelző: a befizetett hónapok aránya. */
const ProgressRing: React.FC<{ ratio: number; label: string }> = ({ ratio, label }) => {
  const r = 34;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative h-24 w-24 shrink-0">
      <svg viewBox="0 0 80 80" className="h-24 w-24 -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" strokeWidth="8" className="stroke-white/25" />
        <circle
          cx="40"
          cy="40"
          r={r}
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          className="stroke-white transition-all duration-700"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.min(1, Math.max(0, ratio)))}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-white">{label}</span>
    </div>
  );
};

const segmented = (active: boolean) =>
  `rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
    active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
  }`;

const chip = (active: boolean) =>
  `whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
    active ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50'
  }`;

/**
 * A szülő (vagy a felnőtt játékos) tagdíjai: havi bontásban vagy éves
 * összesítőben, gyerek és állapot szerint szűrve, egy egyszerű
 * statisztikával arról, hol tart az évben.
 */
const ParentFeesView: React.FC = () => {
  const [view, setView] = useState<View>('monthly');
  const [year, setYear] = useState(thisYear);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [childId, setChildId] = useState<number | 'all'>('all');
  // Évenként tároljuk, hogy az éves nézet és a lapozás ne töltsön újra fölöslegesen
  const [byYear, setByYear] = useState<Record<number, ChildFeeOverview[]>>({});
  const [error, setError] = useState(false);

  const neededYears = useMemo(
    () => (view === 'monthly' ? [year] : Array.from({ length: YEARS_SHOWN }, (_, i) => year - i)),
    [view, year]
  );

  useEffect(() => {
    const missing = neededYears.filter((y) => !(y in byYear));
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map((y) => membershipFeesAPI.getChildrenFees(y).then((data) => [y, data] as const)))
      .then((results) => {
        if (cancelled) return;
        setByYear((prev) => ({ ...prev, ...Object.fromEntries(results) }));
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [neededYears, byYear]);

  const children = useMemo(() => {
    const all = Object.values(byYear).flat();
    const unique = new Map(all.map((c) => [c.playerId, { playerId: c.playerId, name: c.playerName }]));
    return Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name, 'hu'));
  }, [byYear]);
  const names = shortNames(children);
  const multiple = children.length > 1;

  const feesFor = (y: number) =>
    (byYear[y] ?? [])
      .filter((c) => childId === 'all' || c.playerId === childId)
      .flatMap((c) => c.fees.filter((f) => f.year === y));

  const yearFees = feesFor(year);
  const loaded = year in byYear;
  const paid = yearFees.filter((f) => f.isPaid);
  const unpaid = yearFees.filter((f) => !f.isPaid);
  const overdue = unpaid.filter((f) => feeState(f) === 'overdue');
  const paidAmount = paid.reduce((s, f) => s + f.amount, 0);
  const unpaidAmount = unpaid.reduce((s, f) => s + f.amount, 0);
  const ratio = yearFees.length ? paid.length / yearFees.length : 0;

  const monthly = yearFees
    .filter((f) => status === 'all' || (status === 'paid' ? f.isPaid : !f.isPaid))
    .sort((a, b) => b.month - a.month || a.playerName.localeCompare(b.playerName, 'hu'));

  if (error) {
    return (
      <p className="rounded-2xl bg-white p-4 text-[15px] text-gray-600 ring-1 ring-black/5">
        A tagdíjakat most nem sikerült betölteni. Próbáld újra később.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Év és nézet */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setYear((y) => y - 1)}
            aria-label="Előző év"
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <span className="min-w-[4rem] text-center text-lg font-semibold tabular-nums text-gray-900">{year}</span>
          <button
            type="button"
            onClick={() => setYear((y) => y + 1)}
            disabled={year >= thisYear + 1}
            aria-label="Következő év"
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-30"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="inline-flex rounded-xl bg-gray-100 p-1">
          <button type="button" onClick={() => setView('monthly')} aria-pressed={view === 'monthly'} className={segmented(view === 'monthly')}>
            Havi
          </button>
          <button type="button" onClick={() => setView('yearly')} aria-pressed={view === 'yearly'} className={segmented(view === 'yearly')}>
            Éves
          </button>
        </div>
      </div>

      {multiple && (
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          <button type="button" onClick={() => setChildId('all')} className={chip(childId === 'all')}>
            Mindenki
          </button>
          {children.map((c) => (
            <button key={c.playerId} type="button" onClick={() => setChildId(c.playerId)} className={chip(childId === c.playerId)}>
              {names[c.playerId]}
            </button>
          ))}
        </div>
      )}

      {/* Statisztika az évre */}
      <div className="rounded-2xl bg-gradient-to-br from-primary-600 to-primary-700 p-4 text-white shadow-sm">
        {!loaded ? (
          <div className="h-24 animate-pulse rounded-xl bg-white/10" />
        ) : yearFees.length === 0 ? (
          <p className="py-6 text-center text-[15px] text-white/90">{year}-ben még nincs tagdíj.</p>
        ) : (
          <div className="flex items-center gap-4">
            <ProgressRing ratio={ratio} label={`${Math.round(ratio * 100)}%`} />
            <div className="min-w-0 flex-1 space-y-1.5">
              <p className="text-[15px] font-semibold">
                {paid.length} / {yearFees.length} hónap rendezve
              </p>
              <p className="text-sm text-white/85">Befizetve: {formatFt(paidAmount)}</p>
              {unpaidAmount > 0 ? (
                <p className="inline-flex rounded-full bg-white/15 px-2.5 py-0.5 text-sm font-semibold">
                  Hátralék: {formatFt(unpaidAmount)}
                  {overdue.length > 0 && ` · ${overdue.length} lejárt`}
                </p>
              ) : (
                <p className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-sm font-semibold">
                  <CheckCircleIcon className="h-4 w-4" /> Minden rendezve
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {view === 'monthly' ? (
        <>
          <div className="flex gap-2">
            {([
              ['all', 'Mind'],
              ['unpaid', `Fizetetlen${unpaid.length ? ` (${unpaid.length})` : ''}`],
              ['paid', 'Fizetve'],
            ] as const).map(([value, label]) => (
              <button key={value} type="button" onClick={() => setStatus(value)} aria-pressed={status === value} className={chip(status === value)}>
                {label}
              </button>
            ))}
          </div>

          {loaded && monthly.length === 0 ? (
            <p className="rounded-2xl bg-white p-4 text-center text-[15px] text-gray-500 ring-1 ring-black/5">
              {status === 'unpaid' ? 'Nincs fizetetlen tagdíj.' : 'Nincs a szűrésnek megfelelő tagdíj.'}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white ring-1 ring-black/5">
              {monthly.map((fee) => {
                const state = feeState(fee);
                const style = STATE_STYLE[state];
                const Icon = style.icon;
                return (
                  <li key={fee.id} className="flex items-center gap-3 px-4 py-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-50">
                      <span className="text-[13px] font-bold uppercase text-gray-700">{MONTH_NAMES[fee.month - 1].slice(0, 3)}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-medium text-gray-900">
                        {MONTH_NAMES[fee.month - 1]}
                        {multiple && childId === 'all' && <span className="font-normal text-gray-500"> · {names[fee.playerId] ?? fee.playerName}</span>}
                      </p>
                      <p className="text-[13px] text-gray-500">
                        {fee.isPaid && fee.paidAt
                          ? `Befizetve: ${new Date(fee.paidAt).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}`
                          : state === 'overdue'
                            ? 'Még nincs befizetve'
                            : fee.year === thisYear && fee.month === thisMonth
                              ? 'Ebben a hónapban esedékes'
                              : 'Később esedékes'}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-[15px] font-semibold tabular-nums text-gray-900">{formatFt(fee.amount)}</p>
                      <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${style.pill}`}>
                        <Icon className="h-3.5 w-3.5" />
                        {style.label}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : (
        <div className="space-y-3">
          {neededYears.map((y) => {
            const fees = feesFor(y);
            const yPaid = fees.filter((f) => f.isPaid);
            const yUnpaid = fees.filter((f) => !f.isPaid).reduce((s, f) => s + f.amount, 0);
            const byMonth = (m: number) => fees.filter((f) => f.month === m);
            return (
              <div key={y} className="rounded-2xl bg-white p-4 ring-1 ring-black/5">
                <div className="mb-3 flex items-baseline justify-between gap-2">
                  <p className="text-[17px] font-semibold text-gray-900">{y}</p>
                  {!(y in byYear) ? (
                    <span className="text-sm text-gray-400">Betöltés...</span>
                  ) : fees.length === 0 ? (
                    <span className="text-sm text-gray-400">Nincs tagdíj</span>
                  ) : (
                    <span className="text-sm text-gray-600">
                      {formatFt(yPaid.reduce((s, f) => s + f.amount, 0))} befizetve
                      {yUnpaid > 0 && <span className="font-medium text-red-600"> · {formatFt(yUnpaid)} hátralék</span>}
                    </span>
                  )}
                </div>
                {/* 12 hónap egy sorban (mobilon 6+6): a szín mutatja az állapotot */}
                <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-12">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                    const monthFees = byMonth(m);
                    const states = monthFees.map(feeState);
                    const state: FeeState | null = states.length === 0 ? null : states.includes('overdue') ? 'overdue' : states.includes('due') ? 'due' : 'paid';
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => {
                          setYear(y);
                          setView('monthly');
                          setStatus('all');
                        }}
                        title={
                          state
                            ? `${MONTH_NAMES[m - 1]}: ${STATE_STYLE[state].label} (${formatFt(monthFees.reduce((s, f) => s + f.amount, 0))})`
                            : `${MONTH_NAMES[m - 1]}: nincs tagdíj`
                        }
                        className={`flex h-10 flex-col items-center justify-center rounded-lg text-[11px] font-semibold leading-none ${
                          state ? STATE_STYLE[state].tile : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {MONTH_NAMES[m - 1].slice(0, 3)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 text-xs text-gray-500">
            {(['paid', 'due', 'overdue'] as FeeState[]).map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5">
                <span className={`h-3 w-3 rounded ${STATE_STYLE[s].tile}`} />
                {STATE_STYLE[s].label}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded bg-gray-100" />
              Nincs tagdíj
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentFeesView;
